import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsSalaryCounter = ({ onNavigate, userInfo }) => {
    const [activeTab, setActiveTab] = useState('manage'); // 'manage' or 'generate'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data States
    const [teachers, setTeachers] = useState([]);
    const [salaryHistory, setSalaryHistory] = useState([]);

    // Forms & Modal States
    const [showStructureModal, setShowStructureModal] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [structureForm, setStructureForm] = useState({
        basic_salary: '', hra: '', da: '', travel_allowance: '', medical_allowance: '',
        special_allowance: '', pf_deduction: '', professional_tax: '', tds_deduction: '', other_deductions: ''
    });

    const [processMonth, setProcessMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
    const [processYear, setProcessYear] = useState(new Date().getFullYear());
    const [processing, setProcessing] = useState(false);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const years = [2024, 2025, 2026, 2027];

    useEffect(() => {
        fetchTeachers();
    }, []);

    useEffect(() => {
        if (activeTab === 'generate') {
            fetchSalaryHistory();
        }
    }, [activeTab, processMonth, processYear]);

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/salary/teachers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setTeachers(result.data);
            } else {
                setError('Failed to fetch teachers');
            }
        } catch (err) {
            console.error(err);
            setError('Error fetching teachers');
        } finally {
            setLoading(false);
        }
    };

    const fetchSalaryHistory = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/salary/history?month=${processMonth}&year=${processYear}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setSalaryHistory(result.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditStructure = (teacher) => {
        setSelectedTeacher(teacher);
        setStructureForm({
            basic_salary: teacher.basic_salary || 0,
            hra: teacher.hra || 0,
            da: teacher.da || 0,
            travel_allowance: teacher.travel_allowance || 0,
            medical_allowance: teacher.medical_allowance || 0,
            special_allowance: teacher.special_allowance || 0,
            pf_deduction: teacher.pf_deduction || 0,
            professional_tax: teacher.professional_tax || 0,
            tds_deduction: teacher.tds_deduction || 0,
            other_deductions: teacher.other_deductions || 0
        });
        setShowStructureModal(true);
    };

    const handleSaveStructure = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/salary/structure`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    teacher_id: selectedTeacher.id,
                    ...structureForm
                })
            });
            const result = await response.json();
            if (result.success) {
                alert('Salary Structure Saved!');
                setShowStructureModal(false);
                fetchTeachers();
            } else {
                alert(result.message);
            }
        } catch (err) {
            alert('Error saving structure');
        }
    };

    const handleProcessSalary = async (teacher) => {
        if (!window.confirm(`Process salary for ${teacher.name} for ${processMonth} ${processYear}?`)) return;

        try {
            setProcessing(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/salary/process`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    teacher_id: teacher.id,
                    salary_month: processMonth,
                    salary_year: processYear,
                    remarks: 'Auto-processed'
                })
            });
            const result = await response.json();
            if (result.success) {
                alert('Salary Processed Successfully');
                fetchSalaryHistory();
            } else {
                alert(result.message);
            }
        } catch (err) {
            alert('Error processing salary');
        } finally {
            setProcessing(false);
        }
    };

    // Helper to format currency
    const fmt = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0);

    return (
        <Layout role="accounts"
            onNavigate={onNavigate}
            activePage="accounts-salary-counter"
            pageTitle="Salary Counter & Management"
            accountsData={{ name: userInfo?.name || 'Accounts', designation: 'Officer' }} // Placeholder if userInfo missing
        >
            <div style={{ padding: '1rem' }}>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                    <button
                        onClick={() => setActiveTab('manage')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'manage' ? '#238548' : '#e0e0e0',
                            color: activeTab === 'manage' ? 'white' : '#333',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                        }}
                    >
                        Manage Salary
                    </button>
                    <button
                        onClick={() => setActiveTab('generate')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'generate' ? '#238548' : '#e0e0e0',
                            color: activeTab === 'generate' ? 'white' : '#333',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                        }}
                    >
                        Generate Pay Slip
                    </button>
                </div>

                {/* Tab 1: Manage Salary */}
                {activeTab === 'manage' && (
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ color: '#238548', marginTop: 0 }}>Teacher Salary List</h2>
                        {loading ? <p>Loading teachers...</p> : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                                            <th style={{ padding: '12px' }}>Name</th>
                                            <th style={{ padding: '12px' }}>Department</th>
                                            <th style={{ padding: '12px' }}>Designation</th>
                                            <th style={{ padding: '12px', textAlign: 'right' }}>Basic Salary</th>
                                            <th style={{ padding: '12px', textAlign: 'right' }}>Net Salary</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teachers.map(t => (
                                            <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '12px' }}>{t.name}</td>
                                                <td style={{ padding: '12px' }}>{t.department}</td>
                                                <td style={{ padding: '12px' }}>{t.designation}</td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(t.basic_salary)}</td>
                                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#238548' }}>{fmt(t.net_salary)}</td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => handleEditStructure(t)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            background: '#238548',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Assign Salary
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab 2: Generate Pay Slip */}
                {activeTab === 'generate' && (
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ color: '#238548', marginTop: 0 }}>Process Salary & Pay Slips</h2>

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
                            <select
                                value={processMonth}
                                onChange={e => setProcessMonth(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                {months.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <select
                                value={processYear}
                                onChange={e => setProcessYear(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <button
                                onClick={fetchSalaryHistory}
                                style={{ padding: '8px 16px', background: '#333', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Refresh
                            </button>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                                        <th style={{ padding: '12px' }}>Teacher</th>
                                        <th style={{ padding: '12px' }}>Month/Year</th>
                                        <th style={{ padding: '12px', textAlign: 'right' }}>Basic</th>
                                        <th style={{ padding: '12px', textAlign: 'right' }}>Total Allowances</th>
                                        <th style={{ padding: '12px', textAlign: 'right' }}>Total Deductions</th>
                                        <th style={{ padding: '12px', textAlign: 'right' }}>Net Payable</th>
                                        <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                                        <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* List ALL Teachers to allow processing if not yet processed */}
                                    {teachers.map(t => {
                                        const processed = salaryHistory.find(h => h.teacher_id === t.id && h.salary_month === processMonth && parseInt(h.salary_year) === parseInt(processYear));
                                        return (
                                            <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '12px' }}>{t.name} <br /><small style={{ color: '#666' }}>{t.department}</small></td>
                                                <td style={{ padding: '12px' }}>{processMonth} {processYear}</td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(t.basic_salary)}</td>
                                                {processed ? (
                                                    <>
                                                        <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(processed.total_allowances)}</td>
                                                        <td style={{ padding: '12px', textAlign: 'right', color: '#d32f2f' }}>{fmt(processed.total_deductions)}</td>
                                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{fmt(processed.net_salary)}</td>
                                                        <td style={{ padding: '12px', textAlign: 'center' }}><span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>PROCESSED</span></td>
                                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                                            <button style={{ padding: '6px 12px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Print Slip</button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td colSpan="3" style={{ textAlign: 'center', color: '#999' }}>Not Processed</td>
                                                        <td style={{ padding: '12px', textAlign: 'center' }}><span style={{ background: '#ffebee', color: '#c62828', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>PENDING</span></td>
                                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                                            <button
                                                                onClick={() => handleProcessSalary(t)}
                                                                disabled={processing || !t.structure_id}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    background: !t.structure_id ? '#ccc' : '#f57c00',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: !t.structure_id ? 'not-allowed' : 'pointer'
                                                                }}
                                                            >
                                                                {t.structure_id ? 'Process' : 'Set Salary First'}
                                                            </button>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Modal for Salary Structure */}
                {showStructureModal && selectedTeacher && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                    }}>
                        <div style={{
                            background: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '800px',
                            maxHeight: '90vh', overflowY: 'auto'
                        }}>
                            <h2 style={{ marginTop: 0 }}>Assign Salary: {selectedTeacher.name}</h2>
                            <form onSubmit={handleSaveStructure} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <h4 style={{ gridColumn: '1/-1', margin: '10px 0 0', borderBottom: '1px solid #eee' }}>Earnings</h4>

                                <label>
                                    Basic Salary *
                                    <input type="number" required value={structureForm.basic_salary} onChange={e => setStructureForm({ ...structureForm, basic_salary: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    HRA
                                    <input type="number" value={structureForm.hra} onChange={e => setStructureForm({ ...structureForm, hra: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    DA
                                    <input type="number" value={structureForm.da} onChange={e => setStructureForm({ ...structureForm, da: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    Travel Allowance
                                    <input type="number" value={structureForm.travel_allowance} onChange={e => setStructureForm({ ...structureForm, travel_allowance: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    Medical Allowance
                                    <input type="number" value={structureForm.medical_allowance} onChange={e => setStructureForm({ ...structureForm, medical_allowance: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    Special Allowance
                                    <input type="number" value={structureForm.special_allowance} onChange={e => setStructureForm({ ...structureForm, special_allowance: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>

                                <h4 style={{ gridColumn: '1/-1', margin: '10px 0 0', borderBottom: '1px solid #eee' }}>Deductions</h4>

                                <label>
                                    Provident Fund (PF)
                                    <input type="number" value={structureForm.pf_deduction} onChange={e => setStructureForm({ ...structureForm, pf_deduction: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    Professional Tax
                                    <input type="number" value={structureForm.professional_tax} onChange={e => setStructureForm({ ...structureForm, professional_tax: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    TDS
                                    <input type="number" value={structureForm.tds_deduction} onChange={e => setStructureForm({ ...structureForm, tds_deduction: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>
                                <label>
                                    Other Deductions
                                    <input type="number" value={structureForm.other_deductions} onChange={e => setStructureForm({ ...structureForm, other_deductions: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '4px' }} />
                                </label>

                                <div style={{ gridColumn: '1/-1', marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                    <button type="button" onClick={() => setShowStructureModal(false)} style={{ padding: '10px 20px', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                    <button type="submit" style={{ padding: '10px 20px', background: '#238548', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Structure</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
};

export default AccountsSalaryCounter;
